// src/pages/Catalogue.jsx
//
// Hero, the stock browser, how it works, and a general enquiry form.
//
// Search, category and sort all live in the URL (?q=, ?category=, ?sort=), so
// a filtered view can be sent on WhatsApp and survives a refresh. The stock
// is read once and everything else happens in the browser.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { ArrowUpDown, ChevronDown, Phone, Search, X } from "lucide-react";
import { getCategories, getStock } from "../api";
import { useContent } from "../ContentContext";
import { SITE } from "../content";
import { Eyebrow, Reveal, useTitle } from "../components/Bits";
import { Magnetic } from "../components/Effects";
import StockCard, { StockCardSkeleton } from "../components/StockCard";
import StockTag from "../components/StockTag";
import EnquiryForm from "../components/EnquiryForm";
import { SORTS, categoryCounts, matches, sortItems } from "../lib/search";

export default function Catalogue() {
  useTitle("Brooke Requip - Refurbished commercial refrigeration");

  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "featured";

  const [all, setAll] = useState(null); // null while loading
  const [error, setError] = useState(false);
  const [order, setOrder] = useState([]);

  useEffect(() => {
    getStock()
      .then(setAll)
      .catch(() => setError(true));

    // Only the order is taken from this. Counts come from the stock itself so
    // they follow the search, and a failure just leaves the chips alphabetical.
    getCategories()
      .then((cats) => setOrder(cats.map((c) => c.category)))
      .catch(() => {});
  }, []);

  // Search first, so the chip counts describe the searched set
  const searched = useMemo(() => (all || []).filter((it) => matches(it, q)), [all, q]);
  const counts = useMemo(() => categoryCounts(searched, order), [searched, order]);
  const shown = useMemo(
    () => sortItems(category ? searched.filter((it) => it.category === category) : searched, sort),
    [searched, category, sort]
  );

  const newest = useMemo(
    () =>
      all
        ? [...all].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 4)
        : null,
    [all]
  );

  // replace, not push - typing a search should not fill the back button
  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && !(key === "sort" && value === "featured")) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams(new URLSearchParams(), { replace: true });
  const filtering = Boolean(q || category);

  return (
    <>
      <Hero total={all?.length} newest={newest} />

      {/* --- stock -------------------------------------------------------- */}
      <section id="stock" className="scroll-mt-16 border-t border-line pb-16 pt-14 sm:pb-24 sm:pt-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <Eyebrow>In stock</Eyebrow>
              <h2 className="mt-3 font-arch text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                On the floor now
              </h2>
            </div>
            {all && (
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-subtle">
                {filtering ? `${shown.length} of ${all.length}` : all.length}{" "}
                {all.length === 1 ? "unit" : "units"}
              </p>
            )}
          </div>
        </div>

        {/* Sticky under the header, so the filters stay in reach while
            scrolling a long list on a phone */}
        <div className="sticky top-[72px] z-30 mt-8 border-y border-line bg-bg/90 backdrop-blur">
          <div className="mx-auto max-w-6xl px-5 py-3 sm:px-8">
            <div className="flex gap-2.5">
              <SearchBox value={q} onChange={(v) => setParam("q", v)} />
              <SortSelect value={sort} onChange={(v) => setParam("sort", v)} />
            </div>

            {counts.length > 0 && (
              <div className="-mx-5 mt-3 flex gap-2 overflow-x-auto px-5 pb-0.5 sm:mx-0 sm:flex-wrap sm:px-0">
                <Chip on={!category} onClick={() => setParam("category", "")}>
                  All <span className="opacity-60">{searched.length}</span>
                </Chip>
                {counts.map((c) => (
                  <Chip
                    key={c.category}
                    on={category === c.category}
                    onClick={() => setParam("category", category === c.category ? "" : c.category)}
                  >
                    {c.category} <span className="opacity-60">{c.count}</span>
                  </Chip>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          {filtering && all && (
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {q && <Active label={`"${q}"`} onClear={() => setParam("q", "")} />}
              {category && <Active label={category} onClear={() => setParam("category", "")} />}
              <button
                type="button"
                onClick={clearAll}
                className="min-h-[34px] px-2 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle transition hover:text-brooke-red"
              >
                Clear all
              </button>
            </div>
          )}

          <motion.div layout className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {!all && !error &&
              Array.from({ length: 6 }, (_, i) => <StockCardSkeleton key={i} />)}

            <AnimatePresence mode="popLayout">
              {shown.map((it, i) => (
                <motion.div
                  key={it.id}
                  layout
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.35, delay: Math.min(i, 9) * 0.03 }}
                >
                  <StockCard item={it} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {error && (
            <Empty
              title="The stock list did not load"
              body="Refresh the page, or call the office and ask what is on the floor."
            />
          )}

          {all && all.length === 0 && (
            <Empty
              title="Nothing listed right now"
              body="Stock moves quickly. Tell us what you are after below and the office will let you know when something comes in."
            />
          )}

          {all && all.length > 0 && shown.length === 0 && (
            <Empty
              title="Nothing matches that"
              body="Try a different word or another category. If you cannot find it, tell us what you need and the office will look out for it."
              onClear={clearAll}
            />
          )}
        </div>
      </section>

      <How />
      <EnquiryForm />
    </>
  );
}

// ---------------------------------------------------------------------------
// Local state while typing, pushed to the URL after a short pause, so the
// list filters as you type without rewriting the address on every key.
function SearchBox({ value, onChange }) {
  const [text, setText] = useState(value);
  const timer = useRef(null);

  useEffect(() => setText(value), [value]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const update = (v) => {
    setText(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(v.trim()), 200);
  };

  return (
    <label className="relative min-w-0 flex-1">
      <Search
        size={15}
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-subtle"
      />
      <input
        type="search"
        value={text}
        onChange={(e) => update(e.target.value)}
        placeholder="Search make, model or type"
        aria-label="Search stock"
        className="min-h-[44px] w-full rounded-full border border-line bg-surface py-2 pl-10 pr-10 text-[14.5px] text-ink outline-none transition placeholder:text-subtle focus:border-brooke-red focus:ring-2 focus:ring-brooke-red/25 [&::-webkit-search-cancel-button]:hidden"
      />
      {text && (
        <button
          type="button"
          onClick={() => {
            clearTimeout(timer.current);
            setText("");
            onChange("");
          }}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-subtle transition hover:text-ink"
        >
          <X size={14} />
        </button>
      )}
    </label>
  );
}

// Icon-only on a phone so the search box keeps the width, labelled from sm up
function SortSelect({ value, onChange }) {
  return (
    <label className="relative shrink-0">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Sort stock"
        className="min-h-[44px] w-[44px] appearance-none rounded-full border border-line bg-surface font-mono text-[10px] uppercase tracking-[0.12em] text-transparent outline-none transition focus:border-brooke-red focus:ring-2 focus:ring-brooke-red/25 sm:w-auto sm:pl-4 sm:pr-9 sm:text-muted"
      >
        {SORTS.map((s) => (
          <option key={s.value} value={s.value} className="text-ink">
            {s.label}
          </option>
        ))}
      </select>
      <ArrowUpDown
        size={15}
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-subtle sm:hidden"
      />
      <ChevronDown
        size={14}
        className="pointer-events-none absolute right-3.5 top-1/2 hidden -translate-y-1/2 text-subtle sm:block"
      />
    </label>
  );
}

function Active({ label, onClear }) {
  return (
    <span className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full border border-brooke-red/40 bg-brooke-red/10 pl-3 pr-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink">
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Remove ${label}`}
        className="grid h-6 w-6 place-items-center rounded-full text-subtle transition hover:text-brooke-red"
      >
        <X size={12} />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
function Hero({ total, newest }) {
  const { hero, contact } = useContent();

  return (
    <section className="bk-wash relative overflow-hidden">
      <div className="bk-vignette pointer-events-none absolute inset-0" />
      <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-40" />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 overflow-hidden">
        <i className="bk-drift absolute -left-[5%] bottom-0 block h-full w-2/3 rounded-[50%] bg-[radial-gradient(ellipse_at_50%_70%,rgb(var(--bk-led)/0.16),transparent_68%)] blur-2xl" />
      </div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-28 sm:px-8 sm:pb-24 sm:pt-36 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <div>
          <Reveal>
            <Eyebrow>{SITE.division} &middot; Requip</Eyebrow>
            <h1 className="mt-4 max-w-[18ch] font-arch text-4xl font-extrabold leading-[1.02] tracking-tight text-ink sm:text-6xl">
              {hero.heading}
            </h1>
            <p className="mt-6 max-w-[54ch] text-[16px] leading-relaxed text-muted sm:text-[17px]">
              {hero.body}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Magnetic>
                <a
                  href="#stock"
                  className="inline-flex min-h-[50px] items-center justify-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
                >
                  See what is in stock
                </a>
              </Magnetic>
              <a
                href={`tel:${contact.phone}`}
                className="inline-flex min-h-[50px] items-center gap-2 rounded-full border border-line px-6 font-mono text-[10px] uppercase tracking-[0.16em] text-muted transition hover:border-brooke-red hover:text-ink"
              >
                <Phone size={14} />
                {contact.phoneLabel}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <dl className="mt-14 grid max-w-xl grid-cols-3 border-t border-line pt-6">
              <Stat value={total ?? "-"} label="Units listed" />
              <Stat value="2000" label="Brooke since" />
              <Stat value="Gauteng" label="Based in" />
            </dl>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <StockTag items={newest} />
        </Reveal>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">{label}</dt>
      <dd className="mt-1 font-arch text-2xl font-extrabold tabular-nums text-ink sm:text-3xl">
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
function How() {
  const { how } = useContent();

  return (
    <section id="how" className="scroll-mt-20 border-t border-line bg-surface2/50 py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{how.eyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-[20ch] font-arch text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            {how.heading}
          </h2>
        </Reveal>

        <ol className="mt-12 grid gap-8 md:grid-cols-3 md:gap-10">
          {how.steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <li className="border-t border-line pt-5">
                <span className="font-mono text-[11px] font-bold text-led">0{i + 1}</span>
                <h3 className="mt-2 font-arch text-xl font-extrabold tracking-tight text-ink">
                  {s.title}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-muted">{s.body}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function Chip({ on, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`min-h-[36px] shrink-0 whitespace-nowrap rounded-full border px-4 font-mono text-[10px] uppercase tracking-[0.14em] transition ${
        on
          ? "border-brooke-red bg-brooke-red/10 text-ink"
          : "border-line text-muted hover:border-brooke-red/50 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Empty({ title, body, onClear }) {
  return (
    <div className="mt-8 rounded-2xl border border-dashed border-line px-6 py-14 text-center">
      <p className="font-arch text-xl font-extrabold text-ink">{title}</p>
      <p className="mx-auto mt-3 max-w-[46ch] text-[14.5px] leading-relaxed text-muted">{body}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex min-h-[46px] items-center rounded-full bg-brooke-red px-6 font-mono text-[10px] uppercase tracking-[0.16em] text-white transition hover:bg-brooke-redDark"
          >
            Clear filters
          </button>
        )}
        <a
          href="#enquire"
          className="inline-flex min-h-[46px] items-center rounded-full border border-line px-6 font-mono text-[10px] uppercase tracking-[0.16em] text-muted transition hover:border-brooke-red hover:text-ink"
        >
          Tell us what you need
        </a>
      </div>
    </div>
  );
}